import type { ReactNode } from 'react';
import { Button } from '@savvy-kit/components/button';
import { Spinner } from '@savvy-kit/components/spinner';
import { cn } from '@savvy-kit/lib/utils';
import { ChevronRight } from 'lucide-react';

type MoneykitConnectDefaultButtonProps = {
  readonly icon: ReactNode;
  readonly title: string;
  readonly loading?: boolean;
  readonly subtitle?: string;
  readonly onClick: () => void;
  readonly disabled?: boolean;
};

/**
 * Savvy Kit replacement for the legacy MUI `MoneykitConnectDefaultButton`.
 *
 * Renders the dark "Connect your accounts" CTA that sits at the top of the
 * pre-Link add-account modal. The visual contract (icon + title on the first
 * row, dimmed subtitle on the second row, right-aligned chevron) is preserved
 * so the surrounding modal layout reads the same — only the underlying
 * primitives are migrated off MUI onto `@savvy-kit`.
 */
export function MoneykitConnectDefaultButton({
  icon,
  title,
  loading,
  subtitle,
  onClick,
  disabled,
}: MoneykitConnectDefaultButtonProps) {
  return (
    <Button
      variant="default"
      onClick={onClick}
      disabled={disabled || loading}
      className={cn(
        // Vertical proportions tuned to mirror the legacy MUI version
        // (py:2 / subtitle1 / body2 / fontSize="large" chevron) so the
        // CTA reads at the same density as the surrounding modal chrome.
        'text-light h-auto w-full justify-between gap-3 rounded-md px-4 py-4 text-left',
        // Kit Button defaults to whitespace-nowrap (it's optimised for
        // single-line CTAs); the subtitle row needs to wrap on narrow
        // viewports so override it here.
        'whitespace-normal',
        // The MoneyKit CTA renders inside a dark-background card; force the
        // button to be transparent so the card's surface shows through and
        // there's no double-fill.
        'bg-transparent shadow-none hover:bg-white/10',
      )}
      aria-label={title}
    >
      <div className="flex min-w-0 flex-1 flex-col items-start gap-1">
        <div className="flex items-center gap-2">
          {loading ? <Spinner className="text-light size-5" /> : icon}
          <span className="text-base font-medium">{title}</span>
        </div>
        {subtitle !== undefined && subtitle.length > 0 && (
          <span className="text-light/70 text-sm font-normal">{subtitle}</span>
        )}
      </div>
      <ChevronRight className="size-8 shrink-0 self-center" />
    </Button>
  );
}
